import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import { useMediaQuery } from "react-responsive";
import { RemoveScroll } from "react-remove-scroll";
import useWindowSize from "../../hooks/useWindowSize";
import { matchOverlayRoute } from "../Routes";
import ErrorBoundary from "../../functional-components/ErrorBoundary";
import { ErrorDisplaySmall } from "../ErrorDisplay";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { useOverlayContext } from "../../functional-components/OverlayContext";
import Icon from "../Icon";
import "./style.scss";

const GenericFlyout = ({
  id,
  isOpen = false,
  close /* Remember to memoize this function */,
  excludeFromClose = "",
  fillHeight = false,
  minHeight = 6 /* rem */,
  width = 32 /* rem */,
  noCloseBtn = false,
  align = "top",
  offset = 0,
  isMobileSubMenu = false,
  ariaLabel = "",
  children
}: {
  id?: string;
  isOpen?: boolean;
  close: () => void;
  excludeFromClose?: string;
  fillHeight?: boolean;
  minHeight?: number;
  width?: number;
  noCloseBtn?: boolean;
  align?: "top" | "left" | "right" | "top-left" | "top-center";
  offset?: number;
  isMobileSubMenu?: boolean;
  ariaLabel?: string;
  children: ReactNode;
}) => {
  const t = useBjelinTranslations();
  const isSmall = useMediaQuery({ maxWidth: 767.98 });
  const winDim = useWindowSize();
  const { registerFlyoutOpened, registerFlyoutClosed, overlayOpened } =
    useOverlayContext();

  const location = useLocation();
  const _isOpen = useRef(isOpen);
  const isFirst = useRef(true);
  const [below, setBelow] = useState(false);
  const _isBelow = useRef(false);
  const prevLocation = useRef({ ...location });
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const portalId = "flyout-portal-root";
  const contentRef = useRef<HTMLDivElement>(null);

  const baseFontSize: number = parseInt(
    window
      .getComputedStyle(document.documentElement)
      .getPropertyValue("font-size")
      .match(/\d+/)?.[0] || "16"
  );

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
    } else {
      const isOverlayRoute = matchOverlayRoute(location) !== undefined;
      if (isOverlayRoute) {
        _isBelow.current = _isOpen.current;
        setBelow(_isOpen.current);
      } else {
        if (
          location.pathname !== prevLocation.current.pathname &&
          _isOpen.current &&
          !_isBelow.current
        ) {
          // Close on location change while open
          //console.log("location change close");
          prevLocation.current = { ...location };
          close?.();
        }
      }
    }
  }, [location, close]);

  useEffect(() => {
    setTimeout(() => {
      _isOpen.current = isOpen;
    }, 100);
    if (isOpen) {
      _isBelow.current = false;
      setBelow(false);
      registerFlyoutOpened();
      setTimeout(() => {
        /*console.log(
        "first link",
        contentRef.current,
        contentRef.current?.getElementsByTagName("a").item(0)
      );*/
        contentRef.current?.getElementsByTagName("a").item(0)?.focus();
      }, 500);
    } else {
      registerFlyoutClosed();
    }
  }, [isOpen, registerFlyoutClosed, registerFlyoutOpened]);

  useLayoutEffect(() => {
    let _modalRoot = document.getElementById(portalId);
    if (!_modalRoot) {
      _modalRoot = document.createElement("div");
      _modalRoot.setAttribute("id", portalId);
      document.body.append(_modalRoot);
    }
    setModalRoot(_modalRoot);
  }, []);

  const docClickHandler = useCallback(
    (e: MouseEvent) => {
      if (isOpen) {
        const modalElements = document.getElementsByClassName("modal");
        if (
          !modalRoot?.contains(e.target as Node) &&
          !(modalElements[0] && modalElements[0].contains(e.target as Node)) &&
          (!excludeFromClose ||
            !document
              .getElementById(excludeFromClose)
              ?.contains(e.target as Node))
        ) {
          if (!(_isBelow.current && overlayOpened)) {
            //console.log("docClickHandler close");
            close?.();
          }
        }
      }
    },
    [isOpen, modalRoot, excludeFromClose, overlayOpened, close]
  );

  useEffect(() => {
    document.body.addEventListener("click", docClickHandler);
    return () => {
      document.body.removeEventListener("click", docClickHandler);
    };
  }, [docClickHandler]);

  if (!modalRoot) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key={"flyout-cover-" + id}
          id="flyout-cover"
          aria-hidden
          style={{
            zIndex: below ? 0 : 1201,
            top: `calc(var(--padding-top) + ${
              overlayOpened ? 0 : "4rem"
            }px + ${offset}px)`
          }}
          initial={{ opacity: 0, pointerEvents: "all" }}
          animate={{ opacity: 1, pointerEvents: "all" }}
          exit={{ opacity: 0, pointerEvents: "none" }}
          transition={{ type: "spring", damping: 40, stiffness: 400 }}
          onClick={e => {
            if ((e.target as HTMLElement).id === "flyout-cover") {
              close?.();
            }
            e.stopPropagation();
          }}
        ></motion.div>
      )}
      {isOpen && (
        <motion.div
          ref={contentRef}
          key={"flyout-content-" + id}
          id={id ? id : "flyout-content"}
          role="dialog"
          aria-label={ariaLabel}
          className={"flyout-content"}
          initial={{
            opacity: 0,
            ...((isMobileSubMenu && isSmall) || align === "left"
              ? { x: "-100%" }
              : { y: "-100%" }),
            pointerEvents: "none"
          }}
          animate={{
            opacity: 1,
            ...((isMobileSubMenu && isSmall) || align === "left"
              ? { x: 0 }
              : { y: 0 }),
            pointerEvents: "all"
          }}
          exit={{
            opacity: 0,
            ...((isMobileSubMenu && isSmall) || align === "left"
              ? { x: "-100%" }
              : { y: "-100%" }),
            pointerEvents: "none"
          }}
          transition={{ type: "spring", damping: 40, stiffness: 400 }}
          style={{
            zIndex: below ? 11 : 1202,
            ...(isSmall
              ? {
                  top: `calc(var(--menu-height) + var(--padding-top) + ${offset}px)`,
                  bottom: 0,
                  left: 0,
                  right: 0
                }
              : {
                  top: `calc(var(--padding-top) + ${
                    overlayOpened ? 0 : 4
                  }rem + ${offset}px)`,
                  minHeight: minHeight + "rem",
                  maxHeight: `calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - var(--menu-height) - var(--padding-top))`,
                  ...(fillHeight && {
                    bottom: 0
                  }),
                  ...(align === "left"
                    ? { left: 0 }
                    : align === "top-left"
                    ? {
                        left:
                          winDim.innerWidth > 1280
                            ? winDim.innerWidth / 2 - 640
                            : 0,
                        width: width + "rem",
                        maxWidth: "100vw"
                      }
                    : {
                        right:
                          align === "top-center"
                            ? winDim.innerWidth / baseFontSize > width
                              ? (winDim.innerWidth / baseFontSize - width) / 2 +
                                "rem"
                              : 0
                            : winDim.innerWidth > 1280
                            ? winDim.innerWidth / 2 - 640
                            : 0,
                        width: width + "rem",
                        maxWidth: "100vw"
                      })
                })
          }}
        >
          {(!noCloseBtn || (isMobileSubMenu && isSmall)) && (
            <button
              className={"btn-close"}
              style={{
                float: "none",
                position: "absolute",
                zIndex: 10,
                top: "1rem",
                lineHeight: "2rem",
                ...(isMobileSubMenu
                  ? {
                      left: "1.5rem",
                      display: "flex",
                      columnGap: "0.3rem"
                    }
                  : {
                      right: "1.5rem"
                    })
              }}
              onClick={() => {
                close?.();
              }}
            >
              {isMobileSubMenu ? (
                <>
                  <Icon name="chevron-left" type="fal" />{" "}
                  <span
                    style={{
                      display: "inline-block",
                      height: "2rem",
                      lineHeight: "2rem",
                      fontSize: "1rem",
                      fontWeight: 400,
                      verticalAlign: "top"
                    }}
                  >
                    {t("back")}
                  </span>
                </>
              ) : (
                <Icon name="xmark" type="fal" />
              )}
            </button>
          )}
          <ErrorBoundary
            boundaryId={"flyout-" + (id ? id : "unspecified")}
            fallback={<ErrorDisplaySmall />}
          >
            <RemoveScroll enabled={!below && offset === 0}>
              {children}
            </RemoveScroll>
          </ErrorBoundary>
          <KeyboardEventHandler
            handleKeys={["esc"]}
            handleFocusableElements
            onKeyEvent={(key, _e) => {
              switch (key) {
                case "esc":
                  close?.();
                  break;
                default:
              }
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>,
    modalRoot
  );
};

export default GenericFlyout;
